(() => {
  const searchFields =
    'title,product_type,vendor,variants.title,variants.sku';

  const debounce = (callback, wait = 250) => {
    let timeout;
    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback(...args), wait);
    };
  };

  const normaliseSkuSearchTerm = (term) => {
    const value = String(term || '').trim();
    const isSku =
      /^[a-z0-9]{5,}(?:[-_][a-z0-9]{2,})+$/i.test(value) &&
      /[a-z]/i.test(value) &&
      /\d/.test(value);

    return isSku && !value.includes('_') ? `${value}_` : '';
  };

  const normalisePrefixSearchTerm = (term) => {
    const value = String(term || '').trim();
    if (!value || /[*:"()]/.test(value)) return '';

    const words = value.split(/\s+/);
    const lastWord = words.pop();

    if (!/^[a-z0-9\u00c0-\u024f]{2,}$/i.test(lastWord)) return '';

    return [...words, `${lastWord}*`].join(' ');
  };

  const normaliseSubmittedSearchTerm = (term) =>
    normaliseSkuSearchTerm(term) || normalisePrefixSearchTerm(term);

  const redirectNormalisedSearchPage = () => {
    const url = new URL(window.location.href);
    if (!url.pathname.endsWith('/search')) return false;

    const normalisedTerm = normaliseSubmittedSearchTerm(
      url.searchParams.get('q')
    );
    if (!normalisedTerm) return false;

    url.searchParams.set('q', normalisedTerm);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[fields]', searchFields);
    window.location.replace(url.toString());
    return true;
  };

  const initialiseSearchFormNormalisation = () => {
    document.querySelectorAll('form[action]').forEach((form) => {
      if (form.dataset.skuSearchNormalisationInitialised === 'true') return;

      const action = new URL(form.action, window.location.href);
      if (!action.pathname.endsWith('/search')) return;

      const input = form.querySelector('input[name="q"]');
      if (!input) return;

      form.dataset.skuSearchNormalisationInitialised = 'true';
      form.addEventListener('submit', () => {
        const normalisedTerm = normaliseSubmittedSearchTerm(input.value);
        if (normalisedTerm) input.value = normalisedTerm;
      });
    });
  };

  const revealSearchImages = (container) => {
    container.querySelectorAll('img.image-element').forEach((image) => {
      image.style.opacity = '0.7';
    });
  };

  const initialiseHeaderCombinedSearch = (component) => {
    if (component.dataset.combinedSearchInitialised === 'true') return;

    const form = component.querySelector('form[role="search"]');
    const input = component.querySelector('input[name="q"]');
    const results = component.querySelector('#predictive-search');
    const endpoint = component.dataset.combinedSearchUrl;

    if (!form || !input || !results || !endpoint) return;

    component.dataset.combinedSearchInitialised = 'true';
    let controller;

    const clearResults = () => {
      results.innerHTML = '';
      results.style.display = '';
      input.setAttribute('aria-expanded', 'false');
      component.classList.remove('is-active');
    };

    const requestResults = async (query, signal) => {
      const parameters = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][fields]': searchFields,
        'resources[options][prefix]': 'last',
        section_id: 'predictive-search'
      });

      const response = await fetch(`${endpoint}?${parameters.toString()}`, {
        signal,
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) {
        throw new Error(`Combined search failed with status ${response.status}`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');

      return parsed.querySelector('#shopify-section-predictive-search');
    };

    const searchProducts = debounce(async (term) => {
      const query = String(term || '').trim();
      if (!query) {
        clearResults();
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();

      try {
        const requestQuery = normaliseSubmittedSearchTerm(query) || query;
        const section = await requestResults(
          requestQuery,
          controller.signal
        );

        results.innerHTML = section ? section.innerHTML : '';
        revealSearchImages(results);
        results.style.display = 'block';
        input.setAttribute('aria-expanded', 'true');
        component.classList.add('is-active');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('[VOMODA header product search]', error);
          clearResults();
        }
      }
    });

    input.addEventListener(
      'input',
      (event) => {
        event.stopImmediatePropagation();
        searchProducts(event.target.value);
      },
      true
    );

    input.addEventListener('search', (event) => {
      if (!event.target.value) clearResults();
    });

  };

  const initialiseAll = () => {
    initialiseSearchFormNormalisation();
    document
      .querySelectorAll(
        'predictive-search[data-context="header"][data-combined-product-search="true"]'
      )
      .forEach(initialiseHeaderCombinedSearch);
  };

  if (redirectNormalisedSearchPage()) return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', initialiseAll);
})();
